import { AppLayout } from '@/shared/components/layout/app-layout';
import { Navbar } from '@/shared/components/layout/navbar';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Store, RefreshCw, AlertCircle } from 'lucide-react';

interface StoreSkeletonProps {
  onBack: () => void;
}

export function StoreSkeleton({ onBack }: StoreSkeletonProps) {
  return (
    <AppLayout>
      <Navbar onBack={onBack}>
        <div className="flex-1 flex items-center justify-start ml-12 z-10">
          <img src="/logo.svg" alt="Logo" className="h-8 w-auto max-w-32 object-contain" />
        </div>
      </Navbar>

      <div className="flex-1 w-full flex flex-col items-center justify-start">
        {/* Mesma altura inicial da capa (StoreCover, PRAT-154) para a tela não pular. */}
        <div className="w-full bg-gray-100 flex-shrink-0 pb-6">
          <div className="w-full max-h-80" style={{ aspectRatio: 2 }} />
        </div>
        <div className="bg-white rounded-t-2xl translate-y-4 w-full min-h-[60vh] -mt-10 z-12 px-5 py-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
          {/* Skeleton das informações da loja */}
          <div className="flex flex-col justify-center space-y-3">
            <div className="flex gap-3">
              <Skeleton className="w-18 h-18 rounded-xl" />
              <div className="flex flex-col items-start justify-center gap-1 flex-1">
                <Skeleton className="h-4 w-32 mb-2" />
                <div className="flex flex-col items-start justify-center gap-1">
                  <Skeleton className="h-3 w-24 mb-1" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <Skeleton className="h-3 w-8" />
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            </div>

            {/* Skeleton do input de pesquisa */}
            <Skeleton className="w-full h-12 rounded-2xl" />
          </div>

          {/* Skeleton dos itens */}
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-px w-full" />
                </div>
                <div className="space-y-4">
                  {Array.from({ length: 2 }).map((_, itemIndex) => (
                    <div key={itemIndex} className="flex gap-3">
                      <Skeleton className="w-16 h-16 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

interface StoreErrorSkeletonProps extends StoreSkeletonProps {
  /**
   * `true` quando a API disse que a loja não existe (404). Qualquer outra
   * falha (servidor fora, erro 500) não é "loja não encontrada" (PRAT-39).
   */
  notFound?: boolean;
}

export function StoreErrorSkeleton({ onBack, notFound = true }: StoreErrorSkeletonProps) {
  return (
    <AppLayout>
      <Navbar onBack={onBack}>
        <div className="flex-1 flex items-center justify-start ml-12 z-10">
          <img src="/logo.svg" alt="Logo" className="h-8 w-auto max-w-32 object-contain" />
        </div>
      </Navbar>

      <div className="flex-1 w-full flex flex-col items-center justify-center px-5 py-12 animate-in fade-in duration-300">
        <div className="flex flex-col items-center justify-center space-y-6 max-w-sm">
          {/* Ícone de erro */}
          <div className="relative">
            <div className="absolute inset-0 bg-red-100 rounded-full blur-2xl opacity-50" />
            <div className="relative bg-red-50 rounded-full p-6">
              <div className="relative">
                <Store className="w-16 h-16 text-red-500" strokeWidth={1.5} />
                <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
                  <AlertCircle className="w-6 h-6 text-red-500" strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>

          {/* Título e descrição */}
          <div className="flex flex-col items-center space-y-1 text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {notFound ? 'Loja não encontrada' : 'Não foi possível carregar a loja'}
            </h2>
            <p className="text-xs text-gray-500">
              {notFound
                ? 'Verifique o link ou tente novamente'
                : 'Estamos com instabilidade. Tente novamente em instantes'}
            </p>
          </div>

          {/* Botões de ação */}
          <div className="flex flex-col w-full space-y-3 pt-2">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground rounded-lg py-3 px-4 font-medium text-sm hover:bg-primary/90 transition-colors clickable-button"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar novamente
            </button>
            <button
              onClick={onBack}
              className="w-full bg-gray-100 text-gray-700 rounded-lg py-3 px-4 font-medium text-sm hover:bg-gray-200 transition-colors clickable-button"
            >
              Voltar para início
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
